import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Loader2, TrendingUp, Trophy } from "lucide-react";
import { toast } from "sonner";

const tabs = [
  { key: "daily", label: "Täglich" },
  { key: "weekly", label: "Wöchentlich" },
  { key: "monthly", label: "Monatlich" },
];

export default function StatisticsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("daily");

  useEffect(() => {
    api
      .get("/portal/stats")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const seriesLabel = useMemo(() => {
    if (tab === "daily") return "Scans pro Tag (letzte 30 Tage)";
    if (tab === "weekly") return "Scans pro Woche (letzte 12 Wochen)";
    return "Scans pro Monat (letzte 12 Monate)";
  }, [tab]);

  return (
    <div className="space-y-6" data-testid="portal-stats-page">
      <header>
        <p className="label-eyebrow">Analyse</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Statistik</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Wie oft dein QR-Code gescannt wurde und welche Tage am stärksten sind.
        </p>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`stats-tab-${t.key}`}
            className={`px-4 py-2 text-sm rounded-full border transition-colors ${
              tab === t.key
                ? "border-primary/40 bg-primary/10 text-primary"
                : "border-white/10 text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card-surface p-10 text-center text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Lädt…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <KpiSmall label="Scans gesamt" value={stats?.total ?? 0} />
            <KpiSmall label="Scans heute" value={stats?.today ?? 0} tone="primary" />
            <KpiSmall
              label="Bester Tag"
              value={stats?.top_days?.[0]?.count ?? "—"}
              hint={stats?.top_days?.[0]?.date}
            />
          </div>

          <div
            className="card-surface p-4 sm:p-5"
            data-testid={`stats-chart-${tab}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-primary" />
              <h3 className="font-heading font-semibold">{seriesLabel}</h3>
            </div>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                {tab === "daily" ? (
                  <LineChart data={stats?.daily || []}>
                    <CartesianGrid stroke="hsl(240 10% 15%)" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: "hsl(240 5% 65%)" }}
                      tickFormatter={(v) => v.slice(5)}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "hsl(240 5% 65%)" }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(240 10% 8%)",
                        border: "1px solid hsl(240 10% 15%)",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="hsl(217 91% 60%)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                ) : (
                  <BarChart data={tab === "weekly" ? stats?.weekly || [] : stats?.monthly || []}>
                    <CartesianGrid stroke="hsl(240 10% 15%)" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: "hsl(240 5% 65%)" }}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "hsl(240 5% 65%)" }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(240 10% 8%)",
                        border: "1px solid hsl(240 10% 15%)",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="count" fill="hsl(217 91% 60%)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card-surface p-5" data-testid="stats-top-days">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-4 h-4 text-amber-400" />
              <h3 className="font-heading font-semibold">Meist-gescannte Tage</h3>
            </div>
            {stats?.top_days?.length ? (
              <ul className="divide-y divide-white/[0.05]">
                {stats.top_days.map((d, i) => (
                  <li key={d.date} className="py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs grid place-items-center font-semibold">
                        {i + 1}
                      </span>
                      <span className="text-sm">{d.date}</span>
                    </div>
                    <span className="tabular-nums font-heading font-semibold">
                      {d.count}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="py-6 text-center text-sm text-muted-foreground border border-dashed border-white/[0.08] rounded-lg">
                Noch keine Daten.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function KpiSmall({ label, value, tone, hint }) {
  return (
    <div className="card-surface p-4">
      <div className="label-eyebrow">{label}</div>
      <div
        className={`mt-2 text-2xl font-heading font-bold ${
          tone === "primary" ? "text-primary" : ""
        }`}
      >
        {value}
      </div>
      {hint ? <div className="text-[11px] text-muted-foreground mt-1">{hint}</div> : null}
    </div>
  );
}
